
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { favoritesService } from '@/services/favoritesService';
import { toast } from '@/hooks/use-toast';

export const useFavorites = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [favoriteStatus, setFavoriteStatus] = useState<Record<string, boolean>>({});

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const data = await favoritesService.getFavorites(user.id);
      setFavorites(data);
      
      // Update favorite status cache
      const statusMap: Record<string, boolean> = {};
      data.forEach(fav => {
        statusMap[fav.favorite_user_id] = true;
      });
      setFavoriteStatus(statusMap);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast({
        title: 'Error',
        description: 'Failed to load favorites',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (targetUserId: string) => {
    if (!user) return;

    const isFavorited = favoriteStatus[targetUserId];
    
    try {
      if (isFavorited) {
        await favoritesService.removeFavorite(user.id, targetUserId);
        setFavoriteStatus(prev => ({ ...prev, [targetUserId]: false }));
        setFavorites(prev => prev.filter(fav => fav.favorite_user_id !== targetUserId));
        
        toast({
          title: 'Removed from favorites',
          description: 'Profile removed from your favorites',
        });
      } else {
        await favoritesService.addFavorite(user.id, targetUserId);
        setFavoriteStatus(prev => ({ ...prev, [targetUserId]: true }));
        
        toast({
          title: 'Added to favorites',
          description: 'Profile added to your favorites',
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: 'Error',
        description: 'Failed to update favorite status',
        variant: 'destructive'
      });
    }
  };

  const checkFavoriteStatus = async (targetUserId: string) => {
    if (!user) return false;

    try {
      const isFavorited = await favoritesService.isFavorite(user.id, targetUserId);
      setFavoriteStatus(prev => ({ ...prev, [targetUserId]: isFavorited }));
      return isFavorited;
    } catch (error) {
      console.error('Error checking favorite status:', error);
      return false;
    }
  };

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  return {
    favorites,
    loading,
    favoriteStatus,
    toggleFavorite,
    checkFavoriteStatus,
    refetchFavorites: fetchFavorites
  };
};
